// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Download from "@insite/mobius/Icons/Download";
import Eye from "@insite/mobius/Icons/Eye";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { getDocument } from "@store/Framework/Services/StoreAccountService";
import getCatalog from "@store/Framework/Store/Store/Data/ProductCatalogs/Handlers/GetCatalog";
import loadGeneralDocuments from "@store/Framework/Store/Store/Data/ProductCatalogs/Handlers/LoadGeneralDocuments";
import loadProductCatalogs from "@store/Framework/Store/Store/Data/ProductCatalogs/Handlers/LoadProductCatalogs";
import { historyTableProps } from "@store/Themes/common/props/history";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import isEmpty from "lodash/isEmpty";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        productCatalogsState: state.store.data.productCatalogs,
    };
};

const mapDispatchToProps = {
    getCatalog,
    getDocument,
    loadProductCatalogs,
    loadGeneralDocuments,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductCatalogsStyles {
    generalDocumentsWrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    catalogLink?: LinkPresentationProps;
    catalogLinkPdf?: LinkPresentationProps;
    catalogWrapper?: InjectableCss;
}

export const productCatalogsStyles: ProductCatalogsStyles = {
    tableWrapper: historyTableProps.wrapper,
    catalogLink: linkGetActionLinkProps(Download),
    catalogLinkPdf: linkGetActionLinkProps(Eye),
    catalogWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            padding-top: ${x => x.theme.design.typography.spacing.base};
        `,
    },
    generalDocumentsWrapper: {
        css: css`
            padding-top: ${pxToRem(25)};
        `,
    },
};

export interface ExcelModel {
    blob: Blob;
    catalogName: string;
}

const styles = productCatalogsStyles;

const ProductCatalogs: FC<Props> = ({
    productCatalogsState,
    getCatalog,
    loadProductCatalogs,
    loadGeneralDocuments,
}) => {
    const { priceTypes: productCatalogs, generalDocuments, catalog, isLoading } = productCatalogsState;
    const hasProductCatalogs = productCatalogs && !isEmpty(productCatalogs);
    const hasGeneralDocuments = generalDocuments && generalDocuments.length > 0;
    const toasterContext = useContext(ToasterContext);
    const [excelFile, setExcelFile] = useState<ExcelModel | null>(null);
    const [clickedFile, setClickedFile] = useState<string>("");

    React.useEffect(() => {
        if (!productCatalogs) {
            loadProductCatalogs();
        }

        if (!generalDocuments) {
            loadGeneralDocuments();
        }
    }, []);

    React.useEffect(() => {
        if (excelFile) {
            const blob = excelFile["blob"];
            const catalogName = excelFile["catalogName"];
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = catalogName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }, [excelFile]);

    React.useEffect(() => {
        setExcelFile(null);
        const fetchExcel = async (response: { arrayBuffer: () => any }, catalogName: string) => {
            if (!excelFile) {
                const data = await response.arrayBuffer();
                if (data) {
                    const blob = new Blob([data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const excelFile: ExcelModel = { blob, catalogName };
                    setExcelFile(excelFile);
                }
            }
        };
        if (
            catalog &&
            catalog.catalogResponse &&
            catalog.catalogName === clickedFile // if links clicked too fast, state doesn't sync fast enough
        ) {
            const catalogName = catalog.catalogName;
            const response = catalog.catalogResponse;
            const catalogUrl = response.url;
            const nameExtension = catalogName.substring(catalogName.lastIndexOf(".") + 1);

            if (nameExtension === "pdf" && typeof window !== "undefined") {
                window.open(catalogUrl, "_blank");
            }
            if (
                nameExtension === "xls" ||
                nameExtension === "xlsx" ||
                nameExtension === "xltl" ||
                nameExtension === "xlsm"
            ) {
                fetchExcel(response, catalogName);
            }
        }
    }, [productCatalogsState]);

    const trackSelectItem = ga.use.trackEvent({ action: "gaEvent" });

    const catalogClickHandler = (categoryName: string, catalogName: string) => {
        trackSelectItem({
            items: [
                { eventCategory: `Product Catalog: ${categoryName}`, eventAction: "Open", eventLabel: catalogName },
            ],
        });
        if (categoryName && catalogName) {
            setClickedFile(catalogName);
            getCatalog({
                categoryName,
                catalogName,
                onError: () => {
                    toasterContext.addToast({ body: translate("File could not be loaded."), messageType: "warning" });
                },
            });
        }
    };

    const documentClickHandler = (documentName: string) => {
        trackSelectItem({
            items: [
                {
                    eventCategory: `Product Catalog: General Documentation`,
                    eventAction: "Open",
                    eventLabel: documentName,
                },
            ],
        });
        getDocument(documentName);
    };

    if (!hasProductCatalogs && !hasGeneralDocuments) {
        return null;
    }

    return (
        <LoadingOverlay loading={isLoading}>
            {hasProductCatalogs && (
                <StyledWrapper data-test-selector="widget_ProductCatalogs">
                    <CardListHeading heading={translate("Product Catalogs")} />
                    <StyledWrapper {...styles.tableWrapper}>
                        {Object.keys(productCatalogs).map(catalog => {
                            return (
                                <StyledWrapper {...styles.catalogWrapper} key={catalog}>
                                    <Typography forwardAs="h3" variant="h5">
                                        {catalog}
                                    </Typography>
                                    {productCatalogs[catalog].map((doc: any) => {
                                        const docParts = doc.split(".");
                                        const docType = docParts.pop();
                                        const docName = docParts.join(".");
                                        let catalogLink = styles.catalogLink;
                                        let tip = translate("Download this file");
                                        if (docType === "pdf") {
                                            catalogLink = styles.catalogLinkPdf;
                                            tip = translate("View PDF in new window");
                                        }

                                        return (
                                            <div key={doc}>
                                                <Link
                                                    {...catalogLink}
                                                    onClick={() => catalogClickHandler(catalog, doc)}
                                                    title={tip}
                                                >
                                                    {docName}
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {hasGeneralDocuments && (
                <StyledWrapper {...styles.generalDocumentsWrapper} data-test-selector="widget_ProductCatalogs">
                    <CardListHeading heading={translate("Store_ProductCatalogs_GeneralDocumentations")} />
                    <StyledWrapper {...styles.tableWrapper}>
                        {generalDocuments.map(document => {
                            return (
                                <StyledWrapper {...styles.catalogWrapper} key={document}>
                                    <Link {...styles.catalogLink} onClick={() => documentClickHandler(document)}>
                                        {document}
                                    </Link>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductCatalogs),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
